import React, { useState, useMemo } from 'react';
import { localized } from '../i18n/translations';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import NotifyAvailable from "../components/NotifyAvailable";
import CustomImage from '../components/CustomImage';
import SEO from '../components/SEO';

function AllProducts({ cartItems, productsData, addToCart, currency, country, apiCountry, translate, categories }) {
  const [showModal, setShowModal] = useState(false);
  const [notifyProductId, setNotifyProductId] = useState(null);
  const [sortBy, setSortBy] = useState('default');
  const [isLoading, setIsLoading] = useState(true);
  const [partnershipProducts, setPartnershipProducts] = useState([]);

  React.useEffect(() => {
    if (productsData !== undefined && Array.isArray(productsData)) {
      if (productsData.length > 0) {
        setIsLoading(false);
      } else {
        const timer = setTimeout(() => setIsLoading(false), 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [productsData]);

  React.useEffect(() => {
    if (productsData && productsData.length > 0) {
      setPartnershipProducts(productsData.filter(p => p.partnership_atelje === true));
    }
  }, [productsData]);

  const closeModal = () => {
    setShowModal(false);
  };

  const sortProducts = (arr, by) => {
    const copy = [...arr];
    switch (by) {
      case 'price-low':
        return copy.sort((a, b) => {
          const pa = a.is_on_sale && a.sale_discount_percentage > 0 ? a[`discounted_${currency}`] : a[currency];
          const pb = b.is_on_sale && b.sale_discount_percentage > 0 ? b[`discounted_${currency}`] : b[currency];
          return pa - pb;
        });
      case 'price-high':
        return copy.sort((a, b) => {
          const pa = a.is_on_sale && a.sale_discount_percentage > 0 ? a[`discounted_${currency}`] : a[currency];
          const pb = b.is_on_sale && b.sale_discount_percentage > 0 ? b[`discounted_${currency}`] : b[currency];
          return pb - pa;
        });
      case 'date-new':
        return copy.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'date-old':
        return copy.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      default:
        return copy;
    }
  };

  // Group regular products by category, in the order categories appear
  const productsByCategory = useMemo(() => {
    if (!productsData || !Array.isArray(productsData)) return [];

    const regularProducts = productsData.filter(p => !p.partnership_atelje);
    const grouped = {};
    regularProducts.forEach(p => {
      if (!grouped[p.category]) grouped[p.category] = [];
      grouped[p.category].push(p);
    });

    return categories
      .filter(cat => grouped[cat.id] && grouped[cat.id].length > 0)
      .map(cat => ({
        category: cat,
        products: sortProducts(grouped[cat.id], sortBy),
      }));
  }, [productsData, categories, sortBy, currency]);

  const totalProducts = productsByCategory.reduce((sum, g) => sum + g.products.length, 0);

  // Sort partnership products
  const sortedPartnershipProducts = useMemo(() => {
    if (!partnershipProducts || !Array.isArray(partnershipProducts)) return [];
    return sortProducts(partnershipProducts, sortBy);
  }, [partnershipProducts, sortBy, currency]);

  const renderProduct = (product, category) => (
    <div className="group" key={product.id}>
      <Link to={`/proizvod/${category?.path || 'default'}/${product.path}`} className="block">
        <div className="relative bg-white">
          {product.is_on_sale && product.sale_discount_percentage > 0 && (
            <div className="absolute top-2 right-2 z-10">
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                -{product.sale_discount_percentage}%
              </span>
            </div>
          )}
          {((product.stock_tracking_enabled && product.stock_quantity === 0) || !product.is_in_stock) && (
            <div className={`absolute ${product.is_on_sale && product.sale_discount_percentage > 0 ? 'top-10' : 'top-2'} right-2 z-10`}>
              <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                {translate("Sold out")}
              </span>
            </div>
          )}
          <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden">
            <CustomImage
              src={product.imageUrl}
              alt={product.imageAlt}
              className="h-full w-full object-cover rounded-xl transition-transform duration-300 group-hover:scale-105"
            />
          </div>
          <div className="p-2">
            <h3 className="text-base font-medium leading-tight mb-1 text-brownSkin hover:text-brownSkin/80 transition-colors duration-200">
              {localized(product, 'name', country)}
            </h3>
            {product.is_on_sale && product.sale_discount_percentage > 0 ? (
              <div className="flex items-center gap-2">
                <span className="text-brownSkin font-medium">
                  {Number(product[`discounted_${currency}`] || 0).toFixed(2)} {currency}
                </span>
                <span className="text-sm text-gray-500 line-through">
                  {Number(product[currency] || 0).toFixed(2)} {currency}
                </span>
              </div>
            ) : (
              <span className="text-brownSkin font-medium">
                {Number(product[currency] || 0).toFixed(2)} {currency}
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="Bulka Ceramics - Svi Proizvodi"
        description="Pogledajte sve naše ručno rađene keramičke proizvode. Sortiranje po ceni i datumu."
        type="website"
        name="Bulka Ceramics"
      />

      {showModal && <NotifyAvailable onClose={closeModal} notifyProductId={notifyProductId} />}

      {/* Sorting Controls */}
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-brownSkin font-medium">
            {isLoading ? (
              <div className="h-6 bg-gray-200 rounded animate-pulse w-32"></div>
            ) : (
              translate("Showing {count} products").replace('{count}', totalProducts)
            )}
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="SortBy" className="text-brownSkin font-medium text-sm">{translate("Sort by:")}</label>
            <div className="relative">
              <select
                name="sort_by"
                id="SortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none bg-white border border-brownSkin/30 rounded-md px-4 py-2 pr-10 focus:ring-brownSkin focus:border-brownSkin focus:ring-1 text-sm min-w-[200px] text-brownSkin"
              >
                <option value="default">{translate("Featured")}</option>
                <option value="price-low">{translate("Price, low to high")}</option>
                <option value="price-high">{translate("Price, high to low")}</option>
                <option value="date-old">{translate("Date, oldest to newest")}</option>
                <option value="date-new">{translate("Date, newest to oldest")}</option>
              </select>
              <svg className="w-5 h-5 text-brownSkin absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Products grouped by category */}
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="relative bg-white">
                <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden">
                  <div className="h-full w-full bg-gray-200 rounded-xl animate-pulse"></div>
                </div>
                <div className="p-2">
                  <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                  <div className="h-5 bg-gray-200 rounded animate-pulse w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : productsByCategory.length > 0 ? (
          <div className="space-y-16">
            {productsByCategory.map(({ category, products }) => (
              <div key={category.id}>
                <div className="mb-6">
                  <Link to={`/kategorija/${category.path}`} className="inline-flex items-center gap-2 group">
                    <h2 className="text-2xl md:text-3xl font-bold text-brownSkin group-hover:text-brownSkin/80 transition-colors">
                      {category.collection_name || category.titleInCategory || category.title}
                    </h2>
                    <svg className="w-5 h-5 text-brownSkin/50 group-hover:text-brownSkin transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </Link>
                  <div className="w-12 h-0.5 bg-brownSkin/30 mt-2"></div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8">
                  {products.map(product => renderProduct(product, category))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🏺</div>
            <h3 className="text-2xl font-bold text-brownSkin mb-2">{translate("No products")}</h3>
            <p className="text-gray-600 mb-8">{translate("No products currently available to display.")}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-darkerSkin text-brownSkin px-8 py-3 rounded-full font-medium hover:bg-brownSkin hover:text-white transition-all duration-300"
            >
              {translate("Back to home")}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
            </Link>
          </div>
        )}
      </div>

      {/* Partnership Products Section */}
      {partnershipProducts.length > 0 && (
        <div className="bg-bgSkin py-16">
          <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-4">
                Bulkaceramics × AteljeStojanović
              </h2>
              <div className="w-24 h-1 bg-[#facb8c] rounded-full mx-auto mb-6"></div>
              <p className="text-gray-700 text-lg leading-relaxed max-w-3xl mx-auto">
                {translate("Exclusive collection of ceramic products created through the Bulkaceramics and AteljeStojanović collaboration.")}
              </p>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8">
              {sortedPartnershipProducts.map(product => {
                const category = categories.find(cat => cat.id === product.category);
                return renderProduct(product, category);
              })}
            </div>
          </div>
        </div>
      )}

      <Footer categories={categories} />
    </div>
  );
}

export default AllProducts;
